import { Marke } from './Marke.js'

/**
 * Die obere Leiste der öffentlichen Seiten.
 *
 * Sie schwebt über dem Inhalt und besteht aus drei Glaspillen: die Marke
 * links, die Navigation in der Mitte, die beiden Aktionen rechts. Der aktive
 * Eintrag bekommt innerhalb der Pille eine eigene, hellere Fläche – so sieht
 * man, wo man ist, ohne dass ein Strich unter dem Wort nötig wäre.
 *
 * KEIN Eintrag «Start».
 *
 * Das Logo IST der Weg zur Startseite; so ist es auf jeder Seite, die diesem
 * Muster folgt. Ein zusätzlicher Eintrag «Start» daneben ist die zweite Tür
 * ins selbe Zimmer und kostet in der Leiste den Platz, den der wichtigste
 * Eintrag bräuchte.
 *
 * Die Einträge stehen an genau einer Stelle. Vorher schrieb jede Seite ihre
 * eigene Leiste, und dann hiess dasselbe Ziel auf drei Seiten dreimal anders.
 */

export type NavZiel = 'funktionen' | 'branchen' | 'preise' | 'faq'

const EINTRAEGE: Array<{ ziel: NavZiel; wort: string; pfad: string }> = [
  { ziel: 'funktionen', wort: 'So funktioniert’s', pfad: '/funktionen' },
  { ziel: 'branchen', wort: 'Branchen', pfad: '/branchen' },
  { ziel: 'preise', wort: 'Pakete', pfad: '/preise' },
  { ziel: 'faq', wort: 'FAQ', pfad: '/faq' }
]

export function Kopfleiste({
  hier,
  angemeldet,
  aufStart,
  aufPfad,
  aufAnmelden,
  aufStarten
}: {
  /** Welcher Eintrag die aktuelle Seite ist – er bekommt die helle Fläche. */
  hier?: NavZiel
  angemeldet: boolean
  aufStart: () => void
  aufPfad: (pfad: string) => void
  aufAnmelden: () => void
  aufStarten: () => void
}): JSX.Element {
  return (
    <header className="rp-kl">
      <button type="button" className="rp-kl-marke" onClick={aufStart} id="btn-marke">
        <Marke groesse={26} />
      </button>

      <nav className="rp-kl-nav" aria-label="Hauptnavigation">
        {EINTRAEGE.map((e) => (
          <button
            key={e.ziel}
            type="button"
            className={e.ziel === hier ? 'rp-kl-knopf aktiv' : 'rp-kl-knopf'}
            aria-current={e.ziel === hier ? 'page' : undefined}
            id={`nav-${e.ziel}`}
            onClick={() => aufPfad(e.pfad)}
          >
            {e.wort}
          </button>
        ))}
      </nav>

      <div className="rp-kl-aktionen">
        <button type="button" className="rp-kl-anmelden" id="btn-top-login" onClick={aufAnmelden}>
          {angemeldet ? 'Zur App' : 'Anmelden'}
        </button>
        <button type="button" className="rp-kl-start" id="btn-top-starten" onClick={aufStarten}>
          {angemeldet ? 'Zur App' : 'Kostenlos starten'}
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </header>
  )
}

/**
 * Der Seitenkopf: Augenbraue, Titel, Meta-Chip, Vorspann.
 *
 * Alle vier Teile sind zentriert und alle vier optional ausser dem Titel. Die
 * Augenbraue trägt einen Punkt in der Akzentfarbe – er kostet nichts und ist
 * das Zeichen, an dem man die Seiten als zusammengehörig erkennt.
 *
 * Der Titel bricht an einem festen Punkt: was nach `hell` kommt, steht in der
 * Akzentfarbe. Zwei Teile statt eines Strings, weil die Trennung sonst über
 * eine Suche nach dem Punkt liefe – und dann bricht sie beim ersten Titel
 * ohne Punkt.
 */
export function Seitenkopf({
  augenbraue,
  titel,
  hell,
  meta,
  vorspann
}: {
  augenbraue: string
  titel: string
  /** Der zweite Teil des Titels, in der Akzentfarbe. */
  hell?: string
  /** Kleiner Glas-Chip unter dem Titel, etwa die Fassung eines Dokuments. */
  meta?: string
  vorspann?: string
}): JSX.Element {
  return (
    <section className="rp-gk">
      <div className="rp-gk-raster" aria-hidden="true" />
      <p className="rp-augenbraue-punkt">{augenbraue}</p>
      <h1 className="rp-gk-titel">
        {titel}
        {hell && (
          <>
            {' '}
            <span>{hell}</span>
          </>
        )}
      </h1>
      {meta && <p className="rp-gk-chip">{meta}</p>}
      {vorspann && <p className="rp-gk-vorspann">{vorspann}</p>}
    </section>
  )
}
